// 189 -- Enrol a child in a sport. Parent picks a SCHOOL + SPORT only (the coach
// assigns the team at approval, user direction). Submits a PENDING
// EnrollmentRequest (188). For a child already associated with a school the
// school is prefilled + locked; a parent-created child searches for the school.

import { useEffect, useState } from "react";
import * as api from "@/lib/api";
import type { SportPublic } from "@dugads/shared";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { SchoolCrest } from "@/components/school-crest";

type SchoolRef = { id: string; name: string; display_name: string | null; logo_url: string | null; primary_color: string | null };

export function EnrollSportDialog({
  playerId,
  playerName,
  schoolId,
  schoolName,
  onClose,
  onSubmitted,
}: {
  playerId: string;
  playerName: string;
  schoolId: string | null;
  schoolName: string | null;
  onClose: () => void;
  onSubmitted: () => void;
}) {
  const [sports, setSports] = useState<SportPublic[]>([]);
  const [sportId, setSportId] = useState("");

  // School selection: locked to the child's school when known, else searchable.
  const [school, setSchool] = useState<SchoolRef | null>(
    schoolId ? { id: schoolId, name: schoolName ?? "", display_name: null, logo_url: null, primary_color: null } : null,
  );
  const [query, setQuery] = useState("");
  const [results, setResults] = useState<SchoolRef[]>([]);
  const [searching, setSearching] = useState(false);

  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    api.getSports().then((r) => setSports(r.sports)).catch(() => setError("Failed to load sports"));
  }, []);

  async function searchSchools() {
    if (query.trim().length < 2) return;
    setSearching(true);
    try {
      const res = await api.searchSchools(query.trim(), { limit: 10 });
      setResults(res.schools.map((s) => ({ id: s.id, name: s.name, display_name: s.display_name, logo_url: s.logo_url, primary_color: s.primary_color })));
    } finally {
      setSearching(false);
    }
  }

  async function submit() {
    if (!school) {
      setError("Please choose a school");
      return;
    }
    if (!sportId) {
      setError("Please choose a sport");
      return;
    }
    setSaving(true);
    setError(null);
    try {
      await api.submitEnrollmentRequest(playerId, { school_id: school.id, sport_id: sportId });
      onSubmitted();
    } catch (e) {
      setError(e instanceof api.ApiError ? e.message : "Failed to submit signup");
      setSaving(false);
    }
  }

  const schoolLocked = Boolean(schoolId);

  return (
    <div
      className="fixed inset-0 z-[100] flex items-end justify-center overflow-y-auto bg-black/60 p-4 sm:items-center"
      data-dialog="enroll-sport"
    >
      <Card className="max-h-[90vh] w-full max-w-lg overflow-y-auto">
        <CardHeader>
          <CardTitle>Sign {playerName} up for a sport</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-sm text-muted-foreground">
            Choose the school and sport. A coach reviews each signup and places your child in the
            right team -- you will see the status here.
          </p>
          {error && <p className="text-sm text-destructive" data-error>{error}</p>}

          {/* School */}
          <div className="flex flex-col gap-1.5">
            <Label>School</Label>
            {schoolLocked && school ? (
              <div className="flex items-center gap-2 rounded-md border border-input bg-muted/30 px-3 py-2 text-sm" data-field="school-locked">
                <SchoolCrest name={school.name} logoUrl={school.logo_url} primaryColor={school.primary_color} size={20} />
                <span>{school.name}</span>
              </div>
            ) : school ? (
              <div className="flex items-center justify-between gap-2 rounded-md border border-input px-3 py-2 text-sm" data-field="school-selected">
                <span className="flex items-center gap-2">
                  <SchoolCrest name={school.display_name ?? school.name} logoUrl={school.logo_url} primaryColor={school.primary_color} size={20} />
                  {school.display_name ?? school.name}
                </span>
                <button type="button" className="text-xs text-muted-foreground hover:text-destructive" onClick={() => setSchool(null)}>
                  Change
                </button>
              </div>
            ) : (
              <div className="flex flex-col gap-2">
                <div className="flex gap-2">
                  <Input
                    placeholder="Search for a school..."
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); searchSchools(); } }}
                    data-field="school-search"
                  />
                  <Button type="button" variant="outline" onClick={searchSchools} disabled={searching || query.trim().length < 2}>
                    {searching ? "..." : "Search"}
                  </Button>
                </div>
                {results.length > 0 && (
                  <div className="flex flex-col gap-1">
                    {results.map((s) => (
                      <button
                        key={s.id}
                        type="button"
                        className="flex items-center gap-2 rounded-md border border-input px-3 py-1.5 text-left text-sm hover:bg-muted"
                        onClick={() => { setSchool(s); setResults([]); setQuery(""); }}
                        data-action="pick-school"
                      >
                        <SchoolCrest name={s.display_name ?? s.name} logoUrl={s.logo_url} primaryColor={s.primary_color} size={18} />
                        {s.display_name ?? s.name}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Sport */}
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="es-sport">Sport</Label>
            <select
              id="es-sport"
              className="h-10 rounded-md border border-input bg-background px-3 text-sm"
              value={sportId}
              onChange={(e) => setSportId(e.target.value)}
              data-field="sport"
            >
              <option value="">Choose a sport...</option>
              {sports.map((s) => (
                <option key={s.id} value={s.id}>{s.name}</option>
              ))}
            </select>
          </div>

          <p className="text-xs text-muted-foreground">
            You can complete the indemnity form after submitting -- it is shown next to the signup.
          </p>

          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={onClose} disabled={saving}>
              Cancel
            </Button>
            <Button onClick={submit} disabled={saving} data-action="submit-signup">
              {saving ? "Submitting..." : "Submit signup"}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
